import { useNavigate, useLocation } from '@tanstack/react-router';
import { User, Scale } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';

const roles = [
  {
    id: 'client',
    label: 'Mandant',
    icon: User,
    path: '/client',
    color: 'bg-blue-500 hover:bg-blue-600',
    activeColor: 'bg-blue-600 ring-2 ring-blue-300',
  },
  {
    id: 'lawyer',
    label: 'Anwalt',
    icon: Scale,
    path: '/lawyer/bookings',
    color: 'bg-purple-500 hover:bg-purple-600',
    activeColor: 'bg-purple-600 ring-2 ring-purple-300',
  },
];

interface RoleSwitcherProps {
  variant?: 'horizontal' | 'vertical';
  size?: 'sm' | 'md';
  showLabels?: boolean;
  className?: string;
}

export function RoleSwitcher({
  variant = 'horizontal',
  size = 'md',
  showLabels = true,
  className,
}: RoleSwitcherProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Only show switcher if user has both lawyer AND client roles
  const canSwitch = user?.is_lawyer && user?.is_client;
  if (!canSwitch) return null;

  const isActive = (path: string) => {
    if (path === '/client') {
      return location.pathname.startsWith('/client');
    }
    if (path === '/lawyer/bookings') {
      return location.pathname.startsWith('/lawyer');
    }
    return false;
  };

  const handleNavigation = (path: string) => {
    navigate({ to: path });
  };

  const buttonSize = size === 'sm' ? 'p-1.5' : 'p-2';
  const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={cn(
          'flex gap-1',
          variant === 'vertical' && 'flex-col',
          className
        )}
      >
        {roles.map((role) => {
          const Icon = role.icon;
          const active = isActive(role.path);

          const button = (
            <button
              key={role.id}
              onClick={() => handleNavigation(role.path)}
              className={cn(
                'flex items-center gap-1.5 rounded-lg text-white transition-all duration-150',
                buttonSize,
                active ? role.activeColor : role.color,
                showLabels && 'px-3'
              )}
            >
              <Icon className={iconSize} />
              {showLabels && <span className={cn('font-medium', textSize)}>{role.label}</span>}
            </button>
          );

          if (!showLabels) {
            return (
              <Tooltip key={role.id}>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent side={variant === 'vertical' ? 'right' : 'bottom'}>
                  {role.label}
                </TooltipContent>
              </Tooltip>
            );
          }

          return button;
        })}
      </div>
    </TooltipProvider>
  );
}
